import { useNotification } from '@wheely/ui-kit';
import React, { useCallback } from 'react';
import {
  useForm as useFormOriginal,
  FieldValues,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ISubmitFormResult } from '@/shared/models/formSubmit';
import { applyServerValidation } from '@/shared/utils/applyServerValidation';
import { getServerErrorText } from '@/shared/utils/getServerErrorText';

import { scrollToFirstError } from '../scroll';

import { TFormStorageType, TFormStorageConfig, useFormStorage } from './useFormStorage';

const DEFAULT_PRESAVE_STORE: TFormStorageConfig = {
  storeId: '',
  type: 'none',
};

interface IUseFormProps<TFieldValues extends FieldValues> extends UseFormProps<TFieldValues> {
  preSaveStore?: {
    storeId: string;
    type: TFormStorageType;
  };
  onSubmit: (formValues: TFieldValues) => Promise<ISubmitFormResult<TFieldValues>>;
}

interface IUseFormReturn<TFieldValues extends FieldValues> {
  form: UseFormReturn<TFieldValues>;
  handleSubmit: (e?: React.BaseSyntheticEvent) => void;
  showSubmitErrors: (result: ISubmitFormResult<TFieldValues>) => boolean;
}

const useForm = <TFieldValues extends FieldValues = FieldValues>(
  props: IUseFormProps<TFieldValues>,
): IUseFormReturn<TFieldValues> => {
  const { preSaveStore = DEFAULT_PRESAVE_STORE, onSubmit, ...useFormProps } = props;

  const form = useFormOriginal(useFormProps);

  const { showNotification } = useNotification();

  const intl = useIntl();

  const clearStorage = useFormStorage({ config: preSaveStore, form });

  /**
   * Field errors go next to their fields and the rest into a notification —
   * both at once. Returns whether there was anything to show.
   */
  const showSubmitErrors = useCallback(
    (result: ISubmitFormResult<TFieldValues>): boolean => {
      const toText = (messages?: string[]) =>
        (messages ?? []).filter(Boolean).map(message => getServerErrorText(intl, message));

      const fieldErrors = Object.fromEntries(
        Object.entries(result.validationErrors ?? {})
          .map(([field, messages]) => [field, toText(messages as string[] | undefined)])
          .filter(([, messages]) => messages.length > 0),
      );
      const baseErrors = toText(result.baseErrors);

      if (Object.keys(fieldErrors).length > 0) {
        applyServerValidation(form.setError, fieldErrors);
      }

      if (baseErrors.length > 0) {
        showNotification({
          showCloseButton: false,
          dark: true,
          message: baseErrors.map(message => <div key={message}>{message}</div>),
        });
      }

      return Object.keys(fieldErrors).length > 0 || baseErrors.length > 0;
    },
    [form.setError, intl, showNotification],
  );

  const handleSubmitWrapped = useCallback(
    async (formValues: TFieldValues) => {
      const result = await onSubmit(formValues);

      if (!showSubmitErrors(result)) {
        clearStorage();
      }

      return result;
    },
    [onSubmit, clearStorage, showSubmitErrors],
  );

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmitWrapped, scrollToFirstError),
    showSubmitErrors,
  };
};

export { useForm };
